// Date utility functions for consistent date formatting across the application

/**
 * Format date to "19 Dec 2025" format
 * @param {string|Date} dateInput - Date string or Date object
 * @returns {string} Formatted date string
 */

import dayjs from "dayjs";


export const formatDisplayDate = (dateInput) => {
  if (!dateInput) return 'N/A';
  
  try {
    const date = new Date(dateInput);
    if (isNaN(date.getTime())) return 'N/A';
    
    const day = date.getDate();
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();
    
    return `${day} ${month} ${year}`;
  } catch {
    return 'N/A';
  }
};

/**
 * Format date to "10 March 2026" format with full month name
 * @param {string|Date} dateInput - Date string or Date object
 * @returns {string} Formatted date string with full month
 */
export const formatDisplayDateFull = (dateInput) => {
  if (!dateInput) return 'N/A';
  
  try {
    const date = new Date(dateInput);
    if (isNaN(date.getTime())) return 'N/A';
    
    const day = date.getDate();
    const month = date.toLocaleString('en-US', { month: 'long' });
    const year = date.getFullYear();
    
    return `${day} ${month} ${year}`;
  } catch {
    return 'N/A';
  }
};

/**
 * Format date for input fields (YYYY-MM-DD)
 * @param {Date} date - Date object
 * @returns {string} Date string in YYYY-MM-DD format
 */
export const formatInputDate = (date) => {
  if (!date) return '';
  return date.toISOString().split('T')[0];
};

/**
 * Get today's date in YYYY-MM-DD format for input fields
 * @returns {string} Today's date
 */
export const getTodayForInput = () => {
  return new Date().toISOString().split('T')[0];
};

/**
 * Get date X months ago in YYYY-MM-DD format
 * @param {number} months - Number of months ago
 * @returns {string} Date string
 */
export const getMonthsAgoForInput = (months) => {
  const date = new Date();
  date.setMonth(date.getMonth() - months);
  return date.toISOString().split('T')[0];
};

/**
 * Format date with time to "19 Dec 2025, 10:30 AM" format
 * @param {string|Date} dateInput - Date string or Date object
 * @returns {string} Formatted date string with time
 */
export const formatDisplayDateTime = (dateInput) => {
  if (!dateInput) return 'N/A';
  
  try {
    const date = new Date(dateInput);
    if (isNaN(date.getTime())) return 'N/A';
    
    const day = date.getDate();
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();
    const time = date.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    
    return `${day} ${month} ${year}, ${time}`;
  } catch {
    return 'N/A';
  }
};

/**
 * Format date range display
 * @param {string|Date} fromDate - Start date
 * @param {string|Date} toDate - End date
 * @returns {string} Formatted date range
 */
export const formatDateRange = (fromDate, toDate) => {
  return `${formatDisplayDate(fromDate)} - ${formatDisplayDate(toDate)}`;
};



export const calculateExpiryDate = (effectiveDate) => {
  if (!effectiveDate) return null;
  return dayjs(effectiveDate).add(30, "day").toDate();
};

export const validateExpiryDate = (effectiveDate, expiryDate) => {
  if (!effectiveDate || !expiryDate) return false;
  return dayjs(expiryDate).isAfter(dayjs(effectiveDate), "day");
};